import React, { useState } from 'react';
import HomePage from '../pages/HomePage';
import ServiceDetail from '../components/ServiceDetail';
import PackageBooking from '../components/PackageBooking';
import GiftCardSystem from '../components/GiftCardSystem';
import ReviewSystem from '../components/ReviewSystem';
import { usePromotions } from '../hooks/UsePromotion';
import PriceDisplay from '../components/PriceDisplay';
import UseSeo from '../hooks/UseSeo';
import UseServiceSeo from '../hooks/UseServiceSeo';
import AboutPage from '../components/AboutPage';
import { Sparkles, Heart, Leaf, Star, Clock, Check } from 'lucide-react';
import eclipseaLogo from '../assets/logo_visible.png';

const MainPage = ({ services, selectedService, selectedCategory, onServiceSelect }) => {
  const [isPackage, setIsPackage] = useState(false);
  const [showGiftCards, setShowGiftCards] = useState(false);
  const { applyPromotion, loading: promoLoading } = usePromotions();

  /* ── SEO page d'accueil ── */
  UseSeo({
    title: showGiftCards
      ? 'Cartes Cadeaux Massage & Bien-être'
      : selectedService
        ? null  // géré par useServiceSEO
        : 'Massages & Soins Bien-être à Bessines — Spa Eclipsea',
    description: showGiftCards
      ? 'Offrez un moment de bien-être avec nos cartes cadeaux massage de 50€ à 200€. Valables 6 mois sur tous les soins. Envoi immédiat par email.'
      : selectedService
        ? null
        : 'Virginie, praticienne bien-être avec 36 ans d\'expérience, vous accueille au Spa Eclipsea de Bessines pour des massages du monde, soins visage et rituels corps personnalisés.',
    path: showGiftCards ? '/cartes-cadeaux' : '/',
  });

  /* ── SEO page service ── */
  UseServiceSeo({
    service: selectedService,
    categoryName: selectedCategory?.title,
  });

  const [showAboutPage, setShowAboutPage] = useState(false);

  const handleAboutClick = () => {
    setShowAboutPage(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    setShowAboutPage(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleServiceSelect = (service, categoryInfo) => {
    const isPackageService = service?.id >= 36 && service?.id <= 38;
    setIsPackage(isPackageService);
    setShowGiftCards(false);
    setShowAboutPage(false);

    const categoryData = {
      title: categoryInfo?.title || categoryInfo || null,
      info: categoryInfo?.info || null,
      complements: categoryInfo?.complements || null
    };

    onServiceSelect(service, categoryData);
  };

  const handleBack = () => {
    onServiceSelect(null, null);
    setIsPackage(false);
    setShowGiftCards(false);
  };

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGiftCardClick = () => {
    setShowGiftCards(true);
    setShowAboutPage(false);
    onServiceSelect(null, null);
    setIsPackage(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main>
      {showAboutPage ? (
        <AboutPage onBack={handleBackToHome} />
      ) : showGiftCards ? (
        <GiftCardSystem onBack={handleBack} />
      ) : selectedService ? (
        isPackage ? (
          <div className="container mx-auto px-4 py-12">
            <PackageBooking packageInfo={selectedService} onBack={handleBack} />
          </div>
        ) : (
          <div className="container mx-auto px-4 py-12">
            <ServiceDetail
              service={selectedService}
              category={selectedCategory}
              onBack={handleBack}
            />
          </div>
        )
      ) : (
        <>
          <HomePage
            eclipseaLogo={eclipseaLogo}
            scrollToServices={scrollToServices}
            handleGiftCardClick={handleGiftCardClick}
            onAboutClick={handleAboutClick}
          />

          {/* Section Services */}
          <section id="services-section" className="py-20 bg-white">
            <div className="container mx-auto px-4">
              {Object.entries(services).map(([categoryKey, categoryData]) => {
                // Déterminer si c'est un objet avec items ou un tableau direct
                const isObjectCategory = categoryData && typeof categoryData === 'object' && !Array.isArray(categoryData);
                const categoryTitle = isObjectCategory ? categoryKey : categoryKey;
                const categoryInfo = isObjectCategory ? categoryData.info : null;
                const categoryComplements = isObjectCategory ? categoryData.complements : null;
                const serviceList = isObjectCategory ? (categoryData.items || []) : (Array.isArray(categoryData) ? categoryData : []);

                // Ne pas afficher si pas de services
                if (serviceList.length === 0) return null;

                return (
                  <div key={categoryKey} className="mb-20">
                    {/* Titre de la catégorie */}
                    <div className="text-center mb-12">
                      <h2 className="text-3xl md:text-4xl font-light text-purple-900 mb-4">
                        {categoryTitle}
                      </h2>
                      <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto mb-6"></div>

                      {/* Info complémentaire (ex: "(1h : Corps, 1h30 : corps, visage et cuir chevelu)") */}
                      {categoryInfo && (
                        <p className="text-gray-600 italic mb-4 max-w-2xl mx-auto">
                          {categoryInfo}
                        </p>
                      )}

                      {/* Compléments (ex: "Cristalline - Douceur - Jade") */}
                      {categoryComplements && (
                        <p className="text-sm text-purple-600 font-medium mb-4">
                          Compléments disponibles : {categoryComplements}
                        </p>
                      )}
                    </div>

                    {/* Grille de cartes de services */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                      {serviceList.map((service) => {
                        // Appliquer la promotion
                        const promotion = applyPromotion(service.price, categoryTitle);

                        return (
                          <div
                            key={service.id}
                            onClick={() => handleServiceSelect(service, {
                              title: categoryTitle,
                              info: categoryInfo,
                              complements: categoryComplements
                            })}
                            className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border border-gray-100"
                          >
                            {/* Titre du service */}
                            <h3 className="text-xl font-semibold text-purple-900 mb-3 group-hover:text-purple-600 transition-colors min-h-[3.5rem] flex items-center">
                              {service.name}
                            </h3>

                            {/* Description du service */}
                            {service.description && (
                              <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                                {service.description}
                              </p>
                            )}

                            {/* Pied de carte : Durée et Prix */}
                            <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                              <div className="flex items-center gap-2 text-gray-500">
                                <Clock className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm font-medium">{service.duration}</span>
                              </div>

                              {/* Prix avec promotion */}
                              <PriceDisplay
                                price={service.price}
                                categoryName={categoryTitle}
                                promotion={promotion}
                              />
                            </div>

                            {/* Indicateur de promotion (badge) */}
                            {promotion.hasPromotion && (
                              <div className="mt-3 text-center">
                                <span className="inline-block bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                  Promotion en cours !
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <ReviewSystem />
        </>
      )}
    </main>
  );
};

export default MainPage;